import type { LlmModel } from '@saaga/shared/interfaces/llm-model.interface'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { ModelInfoModalities } from './model-info-modalities.atom'
import { ModelInfoStats } from './model-info-stats.atom'

export interface ModelInfoDisplayProps {
  model: LlmModel | undefined
  className?: string
}

const DESCRIPTION_CHAR_LIMIT = 150

export const ModelInfoDisplay = ({ model, className }: ModelInfoDisplayProps): ReactNode => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!model) return null

  const hasLongDescription = model.description.length > DESCRIPTION_CHAR_LIMIT
  const displayDescription =
    hasLongDescription && !isExpanded
      ? `${model.description.slice(0, DESCRIPTION_CHAR_LIMIT)}...`
      : model.description

  return (
    <div className={cn('model-info-display rounded-md border bg-muted/30 p-3 text-sm', className)}>
      <ModelInfoStats
        contextLength={model.contextLength}
        maxCompletionTokens={model.topProvider.maxCompletionTokens}
        modality={model.architecture.modality}
      />
      <ModelInfoModalities
        inputModalityList={model.architecture.inputModalityList}
        outputModalityList={model.architecture.outputModalityList}
        pricing={model.pricing}
      />
      {model.description && (
        <div className="model-info-description">
          <p className="text-xs leading-relaxed text-muted-foreground">{displayDescription}</p>
          {hasLongDescription && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="model-info-expand-button mt-1 h-auto p-0 text-xs text-primary hover:bg-transparent"
            >
              {isExpanded ? 'Show less' : 'Show more'}
              {isExpanded ? (
                <ChevronUpIcon className="ml-1 size-3" />
              ) : (
                <ChevronDownIcon className="ml-1 size-3" />
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

ModelInfoDisplay.displayName = 'ModelInfoDisplay'
